import { useState } from 'react';

import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';

import { EMPLOYEE_PRINT_FIELD_OPTIONS, MANDATORY_EMPLOYEE_PRINT_FIELDS } from 'src/types/employee';

interface IPrintEmployeesDialog {
  isOpen: boolean;
  employeeCount: number;
  // Admins may untick the employee number and name; everyone else cannot.
  isAdmin: boolean;
  onClose: VoidFunction;
  onPrint: (fields: string[]) => Promise<void>;
}

export default function PrintEmployeesDialog({
  isOpen,
  employeeCount,
  isAdmin,
  onClose,
  onPrint,
}: IPrintEmployeesDialog) {
  const [fields, setFields] = useState<string[]>(MANDATORY_EMPLOYEE_PRINT_FIELDS);
  const [isPrinting, setIsPrinting] = useState(false);

  const handleToggle = (field: string) => {
    setFields((prev) =>
      prev.includes(field) ? prev.filter((item) => item !== field) : [...prev, field]
    );
  };

  const handlePrint = async () => {
    setIsPrinting(true);

    try {
      await onPrint(fields);
      onClose();
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Print Employees</DialogTitle>

      <DialogContent sx={{ typography: 'body2' }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Pick the fields to print for {employeeCount} selected{' '}
          {employeeCount === 1 ? 'employee' : 'employees'}.
        </Typography>

        <Stack>
          {EMPLOYEE_PRINT_FIELD_OPTIONS.map((option) => {
            const isMandatory = MANDATORY_EMPLOYEE_PRINT_FIELDS.includes(option.value);

            return (
              <FormControlLabel
                key={option.value}
                label={option.label}
                control={
                  <Checkbox
                    checked={fields.includes(option.value)}
                    disabled={isMandatory && !isAdmin}
                    onChange={() => handleToggle(option.value)}
                  />
                }
              />
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>

        <LoadingButton
          variant="contained"
          loading={isPrinting}
          disabled={fields.length === 0}
          onClick={handlePrint}
        >
          Print
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
